/**
 * User helper utilities for fetching and managing user information
 *
 * Features:
 * - In-flight request deduplication
 * - LRU cache with TTL (15 minutes default)
 * - Batch fetching support
 */

import { logger } from "@/utils/logger";

export interface UserBasicInfo {
  fullName: string | null;
  /** null when the requester may only see the user's name (e.g. an admin). */
  email: string | null;
  phone: string | null;
}

// Cache configuration
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
const MAX_CACHE_SIZE = 1000; // Maximum number of cached users

interface CacheEntry {
  data: UserBasicInfo;
  timestamp: number;
}

// In-memory LRU cache
class UserInfoCache {
  private cache = new Map<string, CacheEntry>();
  private accessOrder: string[] = [];

  set(userId: string, data: UserBasicInfo) {
    // Remove if already exists to update access order
    this.delete(userId);

    // Add to cache
    this.cache.set(userId, {
      data,
      timestamp: Date.now(),
    });
    this.accessOrder.push(userId);

    // Evict oldest if cache is full
    if (this.cache.size > MAX_CACHE_SIZE) {
      const oldest = this.accessOrder.shift();
      if (oldest) {
        this.cache.delete(oldest);
      }
    }
  }

  get(userId: string): UserBasicInfo | null {
    const entry = this.cache.get(userId);
    if (!entry) return null;

    // Check if expired
    const age = Date.now() - entry.timestamp;
    if (age > CACHE_TTL_MS) {
      this.delete(userId);
      return null;
    }

    // Update access order (move to end)
    const index = this.accessOrder.indexOf(userId);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
      this.accessOrder.push(userId);
    }

    return entry.data;
  }

  delete(userId: string) {
    this.cache.delete(userId);
    const index = this.accessOrder.indexOf(userId);
    if (index > -1) {
      this.accessOrder.splice(index, 1);
    }
  }

  clear() {
    this.cache.clear();
    this.accessOrder = [];
  }

  getStats() {
    return {
      size: this.cache.size,
      maxSize: MAX_CACHE_SIZE,
      ttlMs: CACHE_TTL_MS,
    };
  }
}

// Singleton cache and in-flight request tracker
const userCache = new UserInfoCache();
const inFlightRequests = new Map<string, Promise<UserBasicInfo>>();

/**
 * Fetch basic user information (name, email, phone) for a given user ID
 * Uses the service role API endpoint to bypass RLS restrictions
 *
 * Features:
 * - Automatic caching with 15-minute TTL
 * - In-flight request deduplication
 * - LRU eviction when cache is full
 *
 * @param userId - The UUID of the user to fetch information for
 * @param bypassCache - If true, skip cache and force fresh fetch
 * @returns Promise<UserBasicInfo> - Basic user information
 * @throws Error if the API request fails
 */
export async function fetchUserBasicInfo(
  userId: string,
  bypassCache: boolean = false
): Promise<UserBasicInfo> {
  // Check cache first (unless bypassing)
  if (!bypassCache) {
    const cached = userCache.get(userId);
    if (cached) {
      logger.debug("Using cached user info", {
        component: "UserHelpers",
        userId: userId.slice(0, 8),
      });
      return cached;
    }
  }

  // Check if request already in-flight
  const inFlightRequest = inFlightRequests.get(userId);
  if (inFlightRequest) {
    logger.debug("Deduplicating in-flight user info request", {
      component: "UserHelpers",
      userId: userId.slice(0, 8),
    });
    return inFlightRequest;
  }

  // Create new request
  const requestPromise = (async (): Promise<UserBasicInfo> => {
    try {
      const response = await fetch(
        `/api/user/basic-info?userId=${encodeURIComponent(userId)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            // Allow browser caching
            "Cache-Control": "max-age=300, stale-while-revalidate=600",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.message || `Failed to fetch user info: ${response.status}`;
        logger.error(
          `API request failed for user ${userId}:`,
          {
            component: "UserHelpers",
            userId: userId.slice(0, 8),
            status: response.status,
            errorData,
          },
          new Error(errorMessage)
        );
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (!data || !data.data) {
        logger.error(
          `Invalid response format for user ${userId}:`,
          {
            component: "UserHelpers",
            userId: userId.slice(0, 8),
            responseData: data,
          },
          new Error("Invalid response format")
        );
        throw new Error("Invalid response format from server");
      }

      const userInfo = data.data;

      // Cache the result
      userCache.set(userId, userInfo);

      logger.debug("User info fetched and cached", {
        component: "UserHelpers",
        userId: userId.slice(0, 8),
        cacheStats: userCache.getStats(),
      });

      return userInfo;
    } catch (error) {
      logger.error(
        "Error fetching user basic info:",
        { component: "UserHelpers", userId: userId.slice(0, 8) },
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    } finally {
      // Remove from in-flight requests
      inFlightRequests.delete(userId);
    }
  })();

  // Track in-flight request
  inFlightRequests.set(userId, requestPromise);

  return requestPromise;
}

/**
 * Fetch basic information for multiple users
 *
 * Intelligently uses batch endpoint for uncached users and serves
 * cached data immediately for known users.
 *
 * @param userIds - Array of user UUIDs to fetch information for
 * @param useBatch - If true, use batch endpoint for uncached users (default: true)
 * @returns Promise<Map<string, UserBasicInfo>> - Map of user ID to basic info
 */
export async function fetchMultipleUsersBasicInfo(
  userIds: string[],
  useBatch: boolean = true
): Promise<Map<string, UserBasicInfo>> {
  try {
    const uniqueUserIds = [...new Set(userIds)];
    const userInfoMap = new Map<string, UserBasicInfo>();
    const uncachedUserIds: string[] = [];

    // First, collect cached users
    uniqueUserIds.forEach(userId => {
      const cached = userCache.get(userId);
      if (cached) {
        userInfoMap.set(userId, cached);
      } else {
        uncachedUserIds.push(userId);
      }
    });

    // If all users are cached, return immediately (no logging to reduce noise)
    if (uncachedUserIds.length === 0) {
      return userInfoMap;
    }

    // Only log when we actually need to fetch from API
    logger.debug("Fetching multiple users from API", {
      component: "UserHelpers",
      total: uniqueUserIds.length,
      cached: userInfoMap.size,
      uncached: uncachedUserIds.length,
      useBatch,
    });

    // Use batch endpoint for uncached users if enabled and more than 2 users
    if (useBatch && uncachedUserIds.length > 2) {
      try {
        const batchResults = await fetchUserBasicInfoBatch(uncachedUserIds);
        batchResults.forEach((info, userId) => {
          userInfoMap.set(userId, info);
        });
        return userInfoMap;
      } catch (batchError) {
        logger.warn(
          "Batch fetch failed, falling back to individual requests",
          { component: "UserHelpers", uncachedCount: uncachedUserIds.length },
          batchError instanceof Error
            ? batchError
            : new Error(String(batchError))
        );
        // Fall through to individual fetches
      }
    }

    // Fetch uncached users individually (in parallel)
    const promises = uncachedUserIds.map(async userId => {
      try {
        const info = await fetchUserBasicInfo(userId);
        return { userId, info };
      } catch (error) {
        logger.error(
          `Failed to fetch info for user ${userId}:`,
          { component: "UserHelpers", userId: userId.slice(0, 8) },
          error instanceof Error ? error : new Error(String(error))
        );
        return { userId, info: null };
      }
    });

    const results = await Promise.all(promises);

    results.forEach(({ userId, info }) => {
      if (info) {
        userInfoMap.set(userId, info);
      }
    });

    return userInfoMap;
  } catch (error) {
    logger.error(
      "Error fetching multiple users basic info:",
      { component: "UserHelpers" },
      error instanceof Error ? error : new Error(String(error))
    );
    throw error;
  }
}

/**
 * Fetch basic information for multiple users in a single batch request
 *
 * @param userIds - Array of user UUIDs to fetch information for
 * @returns Promise<Map<string, UserBasicInfo>> - Map of user ID to basic info
 */
export async function fetchUserBasicInfoBatch(
  userIds: string[]
): Promise<Map<string, UserBasicInfo>> {
  try {
    const uniqueUserIds = [...new Set(userIds)];

    if (uniqueUserIds.length === 0) {
      return new Map();
    }

    // Build query string with user IDs
    const params = new URLSearchParams();
    params.append("userIds", uniqueUserIds.join(","));

    const response = await fetch(
      `/api/user/basic-info/batch?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          // Allow browser caching
          "Cache-Control": "max-age=300, stale-while-revalidate=600",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.message ||
        `Failed to fetch batch user info: ${response.status}`;
      logger.error(
        `Batch API request failed:`,
        {
          component: "UserHelpers",
          status: response.status,
          userCount: uniqueUserIds.length,
          errorData,
        },
        new Error(errorMessage)
      );
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (!data || !data.data || !data.data.users) {
      logger.error(
        `Invalid batch response format:`,
        { component: "UserHelpers", responseData: data },
        new Error("Invalid response format")
      );
      throw new Error("Invalid response format from server");
    }

    const userInfoMap = new Map<string, UserBasicInfo>();

    // Process batch results and cache them
    Object.entries(data.data.users).forEach(([userId, userInfo]) => {
      const info = userInfo as UserBasicInfo;
      userInfoMap.set(userId, info);
      userCache.set(userId, info);
    });

    logger.debug("Batch user info fetched and cached", {
      component: "UserHelpers",
      count: userInfoMap.size,
      cacheStats: userCache.getStats(),
    });

    return userInfoMap;
  } catch (error) {
    logger.error(
      "Error fetching batch user basic info:",
      { component: "UserHelpers" },
      error instanceof Error ? error : new Error(String(error))
    );
    throw error;
  }
}

/**
 * Clear the user info cache (useful for testing or forced refresh)
 */
export function clearUserInfoCache() {
  userCache.clear();
  inFlightRequests.clear();
  logger.debug("User info cache cleared", { component: "UserHelpers" });
}

/**
 * Get cache statistics (useful for debugging)
 */
export function getUserInfoCacheStats() {
  return userCache.getStats();
}
